import { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useSetAtom } from "jotai";
import { isAuthenticatedAtom, userAccountAtom, rolesAtom, isSupportUserAtom } from "@store/HostAtoms";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import CustomError from "@interfaces/CustomError";
import { ErrorCodes } from "@constants/ErrorCodes";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export function useAuthInitializer() {
  const { instance, accounts, inProgress } = useMsal();
  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
  const setAccount = useSetAtom(userAccountAtom);
  const setRoles = useSetAtom(rolesAtom);
  const setSupportUser = useSetAtom(isSupportUserAtom);
  const appInsights = useAppInsightsContext();

  
  useEffect(() => {
    if (inProgress !== "none") return;

    const init = async () => {
      try {
        if (!accounts || accounts.length === 0) {
          await instance.loginRedirect();
          return;
        }

        const account = instance.getActiveAccount() ?? accounts[0];
        instance.setActiveAccount(account);

        try {
          setAccount(account);
          setIsAuthenticated(true);

          const roles =
            (account.idTokenClaims?.roles as string[] | undefined)
              ?.map(r => r.toLowerCase()) ?? [];

          setRoles(roles);
          setSupportUser(roles.includes("support"));
        } catch (silentError) {
          if (silentError instanceof InteractionRequiredAuthError) {
            await instance.loginRedirect();
          } else {
            throw silentError;
          }
        }

      } catch (error) {
        const customError: CustomError = {
          message: "MSAL initialization or account fetch failed: " + error,
          stack: error as any,
          errorCode: ErrorCodes.AUTHENTICATION_FAILURE,
          componentName: "AuthProvider",
          correlationId: "",
          xUserId: "",
          metaDataId: 0
        };
        appInsights.trackException({
          severityLevel: SeverityLevel.Error,
          properties: customError
        });
      }
    };

    init();
  }, [instance, inProgress, accounts, setIsAuthenticated, setAccount, setRoles, appInsights]);
}
